<script setup lang="ts">
import {inject, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import {getTags as apiGetTags} from "@/Services/Admin/admin-tags";
import {Button, Column, DataTable} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";
import TagFormDialog from "@/Pages/Admin/Tags/TagFormDialog.vue";

type TagLocal = any;

const $date = inject("date") as typeof dayjs;
const router = useRouter();

const state = reactive({
    show: {
        tagFormDialog: false,
        tag: null,
    },
});

const tags = ref<AdminIndex<TagLocal>>();

const getTags = async () => apiGetTags(router.currentRoute.value.query).then((data) => (tags.value = data));
getTags();

const editTag = (tag: TagLocal) => {
    state.show.tag = tag;
    state.show.tagFormDialog = true;
};
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Tags">
                <Button
                    rounded
                    class="ml-auto bg-askr-green text-sm text-white hover:brightness-110"
                    :icon="{
                        icon: 'ic:round-plus',
                        width: '24',
                    }"
                    @click="state.show.tagFormDialog = true"
                >
                    <span class="hidden md:inline">Add Tag</span>
                </Button>
            </PageHeading>
        </div>
        <DataTable
            :data="tags"
            dataKey="id"
            :loading="false"
            @filter="getTags"
            :allowTagSearch="false"
            defaultSort="-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID" :sortable="true" />
            <Column field="name" header="Name" :sortable="true" />
            <Column header="User">
                <template #body="{data}">
                    {{ data.user?.name ?? '-' }}
                </template>
            </Column>
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <div class="flex gap-1">
                        <Button
                            rounded
                            class="h-8 w-8 bg-amber-500 text-white"
                            :icon="{
                                icon: 'clarity:edit-solid',
                                width: '16',
                            }"
                            @click="editTag(data)"
                        />
                        <TableRecordActions
                            :name="data.name"
                            :routes="{
                                delete: route('admin.tags.destroy', data.id),
                            }"
                            @deleted="getTags"
                        />
                    </div>
                </template>
            </Column>
            <template #expansion="{data}">
                <table>
                    <tbody>
                        <tr>
                            <td class="pr-2 font-bold">ID</td>
                            <td>{{ data.id }}</td>
                        </tr>
                        <tr>
                            <td class="pr-2 font-bold">Localized name</td>
                            <td>{{ data.name }}</td>
                        </tr>
                        <tr>
                            <td class="pr-2 font-bold">User</td>
                            <td>{{ data.user?.name ?? '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </DataTable>
        <TagFormDialog v-model:visible="state.show.tagFormDialog" :tag="state.show.tag" @saved="getTags" />
    </div>
</template>
<style scoped></style>
